import React, { useState, useEffect } from 'react';
import { Typography } from '@mui/material';

interface CountdownTimerProps {
  targetTimestamp: string;
  mode?: 'countdown' | 'elapsed';
  /** `brand`: white text on colored backgrounds (default). `paper`: theme text colors for timeline / cards. */
  surface?: 'brand' | 'paper';
}

function formatTimeParts(diff: number): string {
  const days = Math.floor(diff / 86400000);
  const hours = Math.floor((diff / 3600000) % 24);
  const minutes = Math.floor((diff / 60000) % 60);
  const seconds = Math.floor((diff / 1000) % 60);

  const parts: string[] = [];
  if (days > 0) parts.push(`${days}d`);
  parts.push(`${String(hours).padStart(2, '0')}h`);
  parts.push(`${String(minutes).padStart(2, '0')}m`);
  parts.push(`${String(seconds).padStart(2, '0')}s`);
  return parts.join(' ');
}

export const CountdownTimer: React.FC<CountdownTimerProps> = React.memo(({ targetTimestamp, mode = 'countdown', surface = 'brand' }) => {
  const onPaper = surface === 'paper';
  const [display, setDisplay] = useState<string | null>(null);

  useEffect(() => {
    const calculate = () => {
      const target = new Date(targetTimestamp).getTime();
      const now = Date.now();

      if (mode === 'elapsed') {
        const diff = now - target;
        setDisplay(diff > 0 ? formatTimeParts(diff) : null);
      } else {
        const diff = target - now;
        setDisplay(diff > 0 ? formatTimeParts(diff) : null);
      }
    };
    calculate();
    const id = setInterval(calculate, 1000);
    return () => clearInterval(id);
  }, [targetTimestamp, mode]);

  if (!display) {
    return (
      <Typography
        sx={
          onPaper
            ? { fontSize: '0.9rem', fontWeight: 700, color: 'text.secondary' }
            : { fontSize: '1rem', fontWeight: 800, color: '#fff', textShadow: '0 2px 8px rgba(0,0,0,0.3)' }
        }
      >
        {mode === 'elapsed' ? 'Just started' : 'Starting...'}
      </Typography>
    );
  }

  return (
    <>
      <Typography
        sx={
          onPaper
            ? {
                fontSize: '0.65rem',
                fontWeight: 600,
                color: 'text.secondary',
                textTransform: 'uppercase',
                letterSpacing: 1.2,
              }
            : {
                fontSize: '0.6rem',
                fontWeight: 600,
                color: 'rgba(255,255,255,0.7)',
                textTransform: 'uppercase',
                letterSpacing: 1.5,
              }
        }
      >
        {mode === 'elapsed' ? 'Running for' : 'Starts in'}
      </Typography>
      <Typography
        sx={
          onPaper
            ? {
                fontSize: '1rem',
                fontWeight: 800,
                color: 'text.primary',
                lineHeight: 1.2,
                fontFamily: '"Roboto Mono", monospace',
                letterSpacing: 0.5,
              }
            : {
                fontSize: '1.25rem',
                fontWeight: 800,
                color: '#fff',
                lineHeight: 1,
                textShadow: '0 2px 8px rgba(0,0,0,0.3)',
                fontFamily: '"Roboto Mono", monospace',
                letterSpacing: 1,
              }
        }
      >
        {display}
      </Typography>
    </>
  );
});
